import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { FormGroup, Label, Button, ListGroup, ListGroupItem } from "reactstrap";
import Icone from './Icones';
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const EncontrarRotaPanel = ({
  isOpen,
  isMobile = false,
  sheetState = "half",
  onToggleSheet,
  onSheetStateChange,
  origemId,
  destinoId,
  destinosOptions = [],
  getOptionByValue,
  onOrigemChange,
  onDestinoChange,
  onCalcularRota,
  onLimparRota,
  onCompartilhar,
  onFecharRota,
  rotaCalculada,
  instrucoes = [],
  onInverter,
  mostrarCaminhoTexto,
  mode = "full", 
}) => {
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999999999 }),
    menu: (base) => ({ ...base, zIndex: 9999999999 }),
  };

  const selectedOrigem = getOptionByValue
    ? getOptionByValue(origemId)
    : destinosOptions.find((option) => option.value === origemId) || null;

  const selectedDestino = getOptionByValue
    ? getOptionByValue(destinoId)
    : destinosOptions.find((option) => option.value === destinoId) || null;
    
  const isVisible = !!isOpen;
  if (!isVisible) return null;

  const panelRef = useRef(null);
  const dragRef = useRef({
    startY: 0,
    startOffset: 0,
    dragging: false,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0); // px (translateY)
    const dragData = useRef({
      startY: 0,
      currentTranslate: 0,
      active: false
    });
    
  const snaps = useMemo(() => {
    if (typeof window === "undefined") {
      return { collapsed: 84, half: 360, full: 620 };
    }

    const isSheetMode = mode === "sheet";

    return isSheetMode
      ? {
          collapsed: 84,                       // antes: 96
          half: Math.round(window.innerHeight * 0.42), // antes: 0.55
          full: Math.round(window.innerHeight * 0.78), // antes: 0.92
        }
      : {
          collapsed: 96,
          half: Math.round(window.innerHeight * 0.55),
          full: Math.round(window.innerHeight * 0.92),
        };
  }, [mode]);

  // quando muda estado externamente, zera offset do drag
  useEffect(() => {
    setDragOffset(0);
  }, [sheetState]);

  const getHeightForState = (s) => snaps[s] ?? snaps.collapsed;

  const baseFull = snaps.full;
  const currentHeight = snaps[sheetState] || snaps.collapsed;
  const currentTranslate = baseFull - currentHeight;

 const onHandlePointerDown = (e) => {
  if (!isMobile) return;
  
  const clientY = e.clientY ?? e.touches?.[0]?.clientY;
  dragData.current = {
    startY: clientY,
    currentTranslate: currentTranslate,
    active: true
  };

  setIsDragging(true);

  if (e.pointerId && e.currentTarget.setPointerCapture) {
    e.currentTarget.setPointerCapture(e.pointerId);
  }
};

useEffect(() => {
  const onMove = (e) => {
    if (!dragData.current.active) return;
    
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    const deltaY = clientY - dragData.current.startY;
    
    // Limites: não subir além do topo (full) nem descer além do fundo (collapsed)
    const closedTranslate = baseFull - snaps.collapsed;
    const maxScrollUp = -dragData.current.currentTranslate;
    const maxScrollDown = closedTranslate - dragData.current.currentTranslate;
    
    setDragOffset(clamp(deltaY, maxScrollUp, maxScrollDown));
  };

  const onUp = () => {
    if (!dragData.current.active) return;
    
    const totalTranslate = dragData.current.currentTranslate + dragOffset;
    
    // Decidir qual estado está mais próximo
    const candidates = ["full", "half", "collapsed"];
    let best = sheetState;
    let minDistance = Infinity;

    candidates.forEach(s => {
      const targetTranslate = baseFull - snaps[s];
      const distance = Math.abs(totalTranslate - targetTranslate);
      if (distance < minDistance) {
        minDistance = distance;
        best = s;
      }
    });

    dragData.current.active = false;
    setIsDragging(false);
    setDragOffset(0);
    onSheetStateChange?.(best);
  };

  if (isDragging) {
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }

  return () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", onUp);
  };
}, [isDragging, dragOffset, snaps, baseFull, currentTranslate, onSheetStateChange, sheetState]);

  const sheetTransform = isMobile
    ? `translateY(${currentTranslate + dragOffset}px)`
    : undefined;

  const isSheetMode = mode === "sheet";

  return (
    <div
      ref={panelRef}
      className={[
        "route-panel",
        isMobile ? "route-panel--sheet" : "route-panel--desktop",
        isMobile ? `route-panel--${sheetState}` : "",
        isDragging ? "route-panel--dragging" : "", 
        mode === "sheet" ? "route-panel--sheetmode" : "",
      ].join(" ")}
      aria-hidden={!isVisible}
      style={isMobile ? { transform: sheetTransform } : undefined}
    >
      {isMobile && (
        <button
          type="button"
          className="route-panel-sheet-handle"
          onClick={onToggleSheet}
          onPointerDown={onHandlePointerDown}
          onTouchStart={onHandlePointerDown}
          aria-label="Expandir, recolher ou arrastar o painel"
        >
          <span className="route-panel-sheet-handle__bar" />
        </button>
      )}

      <button
        className="route-topbar__close"
        onClick={(e) => {
          e.preventDefault();
          onFecharRota(); 
        }}
        aria-label="Fechar"
        type="button"
      >
        <span aria-hidden>✕</span>
      </button>

      <div className="route-panel-content">
        { !isSheetMode && (
          <div className="route-panel-header">
            <h3 className="m-0">{isSheetMode ? "Sua rota" : "Encontrar Rota"}</h3>
          </div>
        )}

        {!isSheetMode && (
          <>
            <FormGroup>
              <Label htmlFor="select-origem">
                <b>Estou em:</b>
              </Label>

              <Select
                id="select-origem"
                name="select-origem"
                value={selectedOrigem}
                onChange={onOrigemChange}
                options={destinosOptions}
                placeholder="Selecione um ponto..."
                isClearable
                isSearchable
                menuPortalTarget={portalTarget}
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
                maxMenuHeight={280}
                styles={selectStyles}
              />
            </FormGroup>

            <div className="text-center">
              <Button
                size="sm"
                outline
                color="secondary"
                onClick={onInverter}
                title="Inverter origem e destino"
                disabled={!origemId && !destinoId}
              >
                <i className="bx bx-transfer-alt" /> Inverter
              </Button>
            </div>

            <FormGroup>
              <Label htmlFor="select-destino">
                <b>Quero ir para:</b>
              </Label>

              <Select
                id="select-destino"
                name="select-destino"
                value={selectedDestino}
                onChange={onDestinoChange}
                options={destinosOptions}
                placeholder="Selecione um destino..."
                isClearable
                isSearchable
                menuPortalTarget={portalTarget}
                menuPosition="fixed"
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
                maxMenuHeight={280}
                styles={selectStyles}
              />
            </FormGroup>

            <div className="route-panel-actions">
              <Button
                color="primary"
                onClick={onCalcularRota}
                disabled={!origemId || !destinoId}
                className="flex-grow-1"
              >
                Traçar Rota
              </Button>

              {rotaCalculada && (
                <>
                  <Button color="secondary" onClick={onLimparRota}>
                    Limpar
                  </Button>
                  <Button
                    color="success"
                    onClick={onCompartilhar}
                    className="flex-grow-1"
                  >
                    Compartilhar
                  </Button>
                </>
              )}
            </div>
          </>
        )}

        {isSheetMode && (
          <div className="route-panel-actions">
            <Button color="secondary" onClick={onLimparRota}>
              Limpar
            </Button>
            <Button color="success" onClick={onCompartilhar} className="flex-grow-1">
              Compartilhar
            </Button>
          </div>
        )}

        {mostrarCaminhoTexto && instrucoes.length > 0 && (
          <div className="route-panel-steps" style={{ overflowY: 'auto', maxHeight: '100%' }}>
            <h5 className="mb-3">Passo a passo</h5>
            <ListGroup flush>
              {instrucoes.map((instrucao, index) => (
                <ListGroupItem
                  key={index}
                  dangerouslySetInnerHTML={{ __html: instrucao }}
                  className="px-0 py-2"
                />
              ))}
            </ListGroup>
          </div>
        )}

        {isMobile && <div style={{ height: 20 }} />}
      </div>
    </div>
  );
};

export default EncontrarRotaPanel;